/**
 * OA-1 §Section 7 — Settings > Entities
 *
 * Admin form to populate the LC (Lux Construction) and LD (Lux Developments)
 * company blocks used on contractor self-invoices. Fields per block:
 *   name, address, company_number, invoice_email
 *
 * Backend endpoints: GET /api/settings/entities and PUT /api/settings/entities.
 * Backend seeds sensible defaults on first fetch, so a fresh install already
 * shows both entities pre-populated — the admin just fills in the real
 * company_number and invoice_email addresses.
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Loader2, Save, Building2, Mail, Info } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BLANK_ENTITY = (code) => ({
  code,
  name: '',
  address: '',
  company_number: '',
  invoice_email: '',
});

export function EntitiesSettingsTab({ api }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entities, setEntities] = useState({
    LC: BLANK_ENTITY('LC'),
    LD: BLANK_ENTITY('LD'),
  });

  useEffect(() => {
    loadEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEntities = async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings/entities');
      const list = res.data?.entities || [];
      const map = { LC: BLANK_ENTITY('LC'), LD: BLANK_ENTITY('LD') };
      for (const e of list) {
        if (e.code === 'LC' || e.code === 'LD') {
          map[e.code] = {
            code: e.code,
            name: e.name || '',
            address: e.address || '',
            company_number: e.company_number || '',
            invoice_email: e.invoice_email || '',
          };
        }
      }
      setEntities(map);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load entities');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (code, field, value) => {
    setEntities((prev) => ({ ...prev, [code]: { ...prev[code], [field]: value } }));
  };

  const validate = () => {
    for (const code of ['LC', 'LD']) {
      const e = entities[code];
      if (!e.name.trim()) { toast.error(`${code}: company name is required`); return false; }
      if (!e.address.trim()) { toast.error(`${code}: address is required`); return false; }
      if (e.invoice_email && !EMAIL_RE.test(e.invoice_email)) {
        toast.error(`${code}: invoice email is not a valid address`);
        return false;
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.put('/settings/entities', {
        entities: [entities.LC, entities.LD],
      });
      toast.success('Entities saved. Future invoices will use these details.');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="entities-settings">
      <Card className="bg-[#2A3240] border-white/10">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/10 rounded-md">
              <Building2 className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <CardTitle className="text-xl text-white">Company Entities</CardTitle>
              <CardDescription className="text-gray-400">
                These details are printed on every contractor self-invoice PDF and used as the
                &ldquo;invoice-to&rdquo; email destination when sending.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-md text-blue-300 text-sm">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              Contractor invoices are split by project reference prefix:
              <span className="ml-2"><Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px]">LC</Badge> for Lux Construction</span>
              <span className="ml-2"><Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">LD / LUX</Badge> for Lux Developments</span>
              . A contractor who worked both companies in a month raises two separate invoices — one per entity.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LC */}
      <EntityCard
        entity={entities.LC}
        badgeClass="bg-blue-500/20 text-blue-400 border-blue-500/30"
        title="Lux Construction (LC)"
        subtitle="Used for all LC-prefixed project invoices"
        onChange={(field, value) => updateField('LC', field, value)}
      />

      {/* LD */}
      <EntityCard
        entity={entities.LD}
        badgeClass="bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
        title="Lux Developments (LD)"
        subtitle="Used for all LD/LUX-prefixed project invoices"
        onChange={(field, value) => updateField('LD', field, value)}
      />

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="h-12 px-6 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold"
          data-testid="save-entities-btn"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Save Entities</>
          )}
        </Button>
      </div>
    </div>
  );
}

function EntityCard({ entity, badgeClass, title, subtitle, onChange }) {
  return (
    <Card className="bg-[#2A3240] border-white/10" data-testid={`entity-card-${entity.code}`}>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Badge className={`${badgeClass} text-xs`}>{entity.code}</Badge>
          <div>
            <CardTitle className="text-lg text-white">{title}</CardTitle>
            <CardDescription className="text-gray-400 text-xs">{subtitle}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-gray-300">Company name <span className="text-red-400">*</span></Label>
          <Input
            value={entity.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder={`e.g. ${entity.code === 'LC' ? 'Lux Construction (Northampton) Ltd' : 'LUX Developments (Northampton) Ltd'}`}
            className="mt-1 h-11 bg-[#1E2530] border-white/20 text-white"
            data-testid={`entity-${entity.code}-name`}
          />
        </div>
        <div>
          <Label className="text-gray-300">Registered address <span className="text-red-400">*</span></Label>
          <Textarea
            value={entity.address}
            onChange={(e) => onChange('address', e.target.value)}
            rows={2}
            placeholder="Line 1, Line 2, City, Postcode"
            className="mt-1 bg-[#1E2530] border-white/20 text-white"
            data-testid={`entity-${entity.code}-address`}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-gray-300">Company number</Label>
            <Input
              value={entity.company_number}
              onChange={(e) => onChange('company_number', e.target.value)}
              placeholder="e.g. 11917152"
              className="mt-1 h-11 bg-[#1E2530] border-white/20 text-white"
              data-testid={`entity-${entity.code}-company-number`}
            />
          </div>
          <div>
            <Label className="text-gray-300 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Invoice email
            </Label>
            <Input
              type="email"
              value={entity.invoice_email}
              onChange={(e) => onChange('invoice_email', e.target.value)}
              placeholder="invoices@…"
              className="mt-1 h-11 bg-[#1E2530] border-white/20 text-white"
              data-testid={`entity-${entity.code}-invoice-email`}
            />
          </div>
        </div>
        <p className="text-gray-500 text-xs">
          The invoice email is where contractor self-invoices are sent when Resend production is live.
        </p>
      </CardContent>
    </Card>
  );
}
